import { List, ListSettings } from '@/composables/GraphQL';
import { showToastError } from '@/helpers';
import {
  CampaignsAutomation,
  CampaignsBulk,
  OperatorType,
  ShopsCustomersGroupsList,
  ShopsLang,
  ShopsLists, ShopsOrdersStatus, ShopsProductsCategoriesList,
  ShopsTags,
} from '@/types/generated-types/graphql';
import { CampaignAutomationStatusEnum, CampaignsBulkStatusEnum } from '@/types/enums';
import { UserState, getShopDefaultLang, getUserLang } from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import getCountries from '@/configs/countries';
import { nestCall, NestApiType } from '@/composables/nestApi';

const fetchList = async <T>(name: string, settings: ListSettings, fields: string[]): Promise<T[]> => {
  const res = await List<T>(
    {
      name,
      settings,
      fields,
    },
  );
  if (res.err) {
    await showToastError('GENERIC_ERROR');
  } else {
    return res.items;
  }
  return [];
};

export const fetchShopsLang = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      {
        field: 'id_shop',
        operator: OperatorType.Equals,
        value: UserState.activeShop ? UserState.activeShop.id : 0,
      },
    ],
  };

  const res = await List<ShopsLang>(
    {
      name: 'ShopsLang',
      settings,
      fields: ['lang'],
    },
  );
  if (res.err) {
    await showToastError('GENERIC_ERROR');
  } else {
    return res.items.map((item) => ({
      label: item.lang,
      value: item.lang,
    }));
  }
  return [];
};

export const fetchCountries = async (t: Function) => getCountries(t);

export const fetchLists = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      {
        field: 'id_shop',
        operator: OperatorType.Equals,
        value: UserState.activeShop ? UserState.activeShop.id : 0,
      },
    ],
  };
  const res = await fetchList<ShopsLists>('ShopsLists', settings, ['id_shop_list', 'name']);

  if (res && res.length !== 0) {
    return res.map((item) => ({
      label: item.name,
      value: item.id_shop_list,
    }));
  }
  return [];
};

export const fetchGroups = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
      { field: 'active', operator: OperatorType.Equals, value: '1' },
      { field: 'lang', operator: OperatorType.Equals, value: getShopDefaultLang() },
    ],
  };
  const res = await fetchList<ShopsCustomersGroupsList>('ShopsCustomersGroupsList', settings, ['id_group', 'name']);

  if (res && res.length !== 0) {
    return res.map((item) => ({
      label: item.name,
      value: item.id_group,
    }));
  }
  return [];
};

export const fetchTags = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
    ],
  };
  const res = await fetchList<ShopsTags>('ShopsTags', settings, ['id_shop_tag', 'tag']);

  if (res && res.length !== 0) {
    return res.map((item) => ({
      label: item.tag,
      value: item.id_shop_tag,
    }));
  }
  return [];
};

export const fetchOrderStatus = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      {
        field: 'id_shop',
        operator: OperatorType.Equals,
        value: UserState.activeShop ? UserState.activeShop.id : 0,
      },
    ],
  };
  const res = await fetchList<ShopsOrdersStatus>('ShopsOrdersStatus', settings, ['id_status', 'name', 'lang']);

  if (!res || res.length === 0) {
    return [];
  }

  // shops_orders_status holds one row per (id_status, lang). Collapse to a
  // single option per id_status so the dropdown has no duplicates, preferring
  // the connected user's language, then the shop default, then any remaining
  // language (so the list is never empty when the user's language is missing).
  const userLang = getUserLang();
  const shopLang = getShopDefaultLang();
  const langRank = (lang: string): number => {
    if (lang === userLang) return 0;
    if (lang === shopLang) return 1;
    return 2;
  };

  const byStatus = new Map<string, { label: string; value: string; rank: number }>();
  res.forEach((item: any) => {
    const rank = langRank(item.lang);
    const current = byStatus.get(item.id_status);
    if (!current || rank < current.rank) {
      byStatus.set(item.id_status, { label: item.name, value: item.id_status, rank });
    }
  });

  return [...byStatus.values()].map(({ label, value }) => ({ label, value }));
};

// Cache for the data sources (raw rows of GET /data-sources) — avoids redundant
// API calls when multiple components/themes request the same data within a
// short time window. Shared by fetchDataSources and fetchDataSourcesWithInactive.
const DATA_SOURCES_CACHE_TTL = 30_000; // 30 seconds

/** Row of GET /data-sources. */
interface DataSourceRow {
  id_data_source: number;
  label: string;
  parent_id: number | null;
  active?: boolean;
}

let dataSourcesCache: DataSourceRow[] | null = null;
let dataSourcesCacheTimestamp = 0;
let dataSourcesCachePromise: Promise<DataSourceRow[]> | null = null;

export function invalidateDataSourcesCache(): void {
  dataSourcesCache = null;
  dataSourcesCacheTimestamp = 0;
  dataSourcesCachePromise = null;
}

const fetchDataSourceRowsFromApi = async (): Promise<DataSourceRow[]> => {
  try {
    const res = await nestCall('get', NestApiType.CLIENT_API, '/data-sources');
    if (res && res.data && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (_) {
    // Silently fail — data sources are optional
  }
  return [];
};

const fetchDataSourceRows = async (): Promise<DataSourceRow[]> => {
  // Return cached data if still fresh
  if (dataSourcesCache && (Date.now() - dataSourcesCacheTimestamp) < DATA_SOURCES_CACHE_TTL) {
    return dataSourcesCache;
  }

  // Deduplicate concurrent calls: if a fetch is already in flight, share its promise
  if (dataSourcesCachePromise) {
    return dataSourcesCachePromise;
  }

  dataSourcesCachePromise = fetchDataSourceRowsFromApi();

  try {
    const result = await dataSourcesCachePromise;
    dataSourcesCache = result;
    dataSourcesCacheTimestamp = Date.now();
    return result;
  } finally {
    dataSourcesCachePromise = null;
  }
};

const toDataSourceOptions = (rows: DataSourceRow[], includeInactive: boolean) => {
  const kept = includeInactive ? rows : rows.filter((item) => item.active !== false);

  // Build labels with hierarchy: children show "Parent > Child"
  const parentMap = new Map<number, string>();
  kept.forEach((item) => {
    parentMap.set(item.id_data_source, item.label);
  });

  return kept.map((item) => {
    const parentLabel = item.parent_id ? parentMap.get(item.parent_id) : null;
    return {
      label: parentLabel ? `${parentLabel} > ${item.label}` : item.label,
      shortLabel: item.label,
      value: item.id_data_source,
      parentId: item.parent_id || null,
      ...(includeInactive ? { active: item.active !== false } : {}),
    };
  });
};

/** Active data sources only. */
export const fetchDataSources = async () => toDataSourceOptions(await fetchDataSourceRows(), false);

/**
 * Every data source, deactivated ones included and flagged `active: false`:
 * statistics keep the history of a store that has been closed.
 */
export const fetchDataSourcesWithInactive = async () => toDataSourceOptions(await fetchDataSourceRows(), true);

export const fetchCategories = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      {
        field: 'id_shop',
        operator: OperatorType.Equals,
        value: UserState.activeShop ? UserState.activeShop.id : 0,
      },
    ],
  };
  const res = await fetchList<ShopsProductsCategoriesList>('ShopsProductsCategoriesList', settings, ['id_category', 'name']);

  if (res && res.length !== 0) {
    return res.map((item: any) => ({
      label: item.name,
      value: item.id_category,
    }));
  }
  return [];
};

// Criterion is "sends happened", not "still running": a suspended or archived
// campaign keeps its historical opens, so excluding it would hide contacts.
const OPENABLE_BULK_STATUSES = [
  CampaignsBulkStatusEnum.SENDING,
  CampaignsBulkStatusEnum.SENT,
  CampaignsBulkStatusEnum.SUSPENDED_BY_USER,
  CampaignsBulkStatusEnum.SUSPENDED,
  CampaignsBulkStatusEnum.ARCHIVED_BY_USER,
  CampaignsBulkStatusEnum.ARCHIVED,
];

/** Same rule for scenarios: a disabled or archived one still has past sends. */
const OPENABLE_AUTOMATION_STATUSES = [
  CampaignAutomationStatusEnum.ACTIVE,
  CampaignAutomationStatusEnum.DISABLED,
  CampaignAutomationStatusEnum.ARCHIVED,
];

export const fetchCampaignsBulk = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [{ field: 'date_send', type: 'DESC' }],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
      { field: 'status', operator: OperatorType.In, value: OPENABLE_BULK_STATUSES.join(',') },
    ],
  };
  const res = await fetchList<CampaignsBulk>('CampaignsBulk', settings, ['id_campaign_bulk', 'name']);
  if (res && res.length !== 0) {
    return res.map((item) => ({ label: item.name, value: item.id_campaign_bulk }));
  }
  return [];
};

export const fetchCampaignsAutomation = async () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [{ field: 'name', type: 'ASC' }],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
      { field: 'status', operator: OperatorType.In, value: OPENABLE_AUTOMATION_STATUSES.join(',') },
    ],
  };
  const res = await fetchList<CampaignsAutomation>('CampaignsAutomation', settings, ['id_campaign_automation', 'name']);
  if (res && res.length !== 0) {
    return res.map((item) => ({ label: item.name, value: item.id_campaign_automation }));
  }
  return [];
};
